import type { PrincipalType } from '../types';

// CloudFront sits in front of the API with the managed WAF rule
// SizeRestrictions_QUERYSTRING, which rejects any query string over 2048 bytes
// with a 403 before the request reaches the app. Measured against develop2:
// 2048 bytes gets through, 2049 does not. CloudFront's own 8192-byte URL limit
// (a 414) is four times further away, so this is the one that bites.
export const MAX_QUERY_STRING_BYTES = 2048;

// Thrown instead of sending a request the edge will reject, so the page can say
// what is wrong rather than showing a generic failure.
export class FilterTooLongError extends Error {
  readonly queryStringBytes: number;
  readonly limitBytes = MAX_QUERY_STRING_BYTES;

  constructor(queryStringBytes: number) {
    super(
      `Filter query string is ${queryStringBytes} bytes, over the ${MAX_QUERY_STRING_BYTES}-byte limit`,
    );
    this.name = 'FilterTooLongError';
    this.queryStringBytes = queryStringBytes;
  }
}

const VALID_PRINCIPAL_TYPES = new Set<PrincipalType>([
  'aws_account',
  'aws_role',
  'aws_user',
  'public',
  'conditional',
  'aws_org',
  'oidc',
  'saml',
  'cognito_identity',
  'canonical_user',
  'external_idp',
  'aws_service',
  'aws_support',
]);

function sanitizePrincipalTypes(values: string[]): string[] {
  return values
    .map((value) => (value === 'anonymous' ? 'public' : value))
    .filter((value): value is PrincipalType =>
      // Set.has is invariant in its argument type, so it can't accept the wider
      // string from URLSearchParams without the cast. The .filter type predicate
      // that follows is the real narrowing — values that don't pass .has() get
      // dropped from the result.
      VALID_PRINCIPAL_TYPES.has(value as PrincipalType),
    );
}

// Filter param names shared by URL (useGroupedFilters) and API query string.
// Order mirrors the column / sidebar order in access-grants-table.tsx +
// access-grants-filters.tsx so the three stay in sync as filters get added
// or removed.
const FILTER_PARAMS = [
  'assetName',
  'principal',
  'mechanism',
  'grantScope',
  'grantOrigin',
  'trustStatus',
  'assetType',
  'principalType',
  'resourceAccountId',
  'principalAccountId',
  'reviewDecision',
  'grantee',
  'nextReviewFrom',
  // Also what the Past due chip sets — it is the sidebar's "to" bound.
  'nextReviewAtEnd',
];

// True when the selection already includes every value the field offers, which
// makes the filter a no-op. Selecting all 464 resource types spells out ~18 KB
// of query string to ask for exactly what asking for nothing returns, so the
// param is dropped instead. Superset rather than equality, so a stale value
// carried in from an older URL doesn't defeat the check.
export function coversEveryValue(
  selected: string[],
  available: string[] | undefined,
): boolean {
  if (!available?.length) {
    return false;
  }
  const chosen = new Set(selected);
  return available.every((value) => chosen.has(value));
}

export type GrantsQueryOptions = {
  limit: number;
  cursor?: string;
  // Every resource type the tenant can filter by, from useListResourceTypes.
  // Needed to recognise a fully-covered Resource type filter; without it that
  // filter is sent as selected.
  allResourceTypes?: string[];
  // The global filter's selection, from useGrantsScope.
  scope?: Record<string, string>;
};

/**
 * Builds the query string for a grants request from the page URL.
 *
 * Throws `FilterTooLongError` rather than sending a request the WAF will reject
 * — the caller can then tell the user which filter to trim.
 */
export function buildGrantsQuery(
  search: string,
  { limit, cursor, allResourceTypes, scope }: GrantsQueryOptions,
): URLSearchParams {
  const urlParams = new URLSearchParams(search);
  const params = new URLSearchParams({ limit: String(limit) });

  // multiSelect filters write repeated params (?trustStatus=a&trustStatus=b);
  // the backend expects comma-separated (?trustStatus=a,b)
  for (const key of FILTER_PARAMS) {
    const raw = urlParams.getAll(key);
    if (key === 'assetType' && coversEveryValue(raw, allResourceTypes)) {
      continue;
    }
    const values = (
      key === 'principalType' ? sanitizePrincipalTypes(raw) : raw
    ).join(',');
    if (values) {
      params.set(key, values);
    }
  }

  const searchText = urlParams.get('search');
  if (searchText) {
    params.set('search', searchText);
  }

  for (const [key, value] of Object.entries(scope ?? {})) {
    params.set(key, value);
  }

  if (cursor) {
    params.set('cursor', cursor);
  }

  // toString percent-encodes to ASCII, so length is the byte count the edge
  // measures.
  const queryString = params.toString();
  if (queryString.length > MAX_QUERY_STRING_BYTES) {
    throw new FilterTooLongError(queryString.length);
  }

  return params;
}
